/* App.js*/
import React, { useState, useEffect } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import EnterpriseSelectPage from './pages/enterprise/EnterpriseSelectPage';
import EnterpriseAppLayout from './pages/enterprise/EnterpriseAppLayout';
import EnterpriseDashboardPage from './pages/enterprise/EnterpriseDashboardPage';
import EnterpriseTeamsPage from './pages/enterprise/EnterpriseTeamsPage';
import EnterpriseTeamDetailPage from './pages/enterprise/EnterpriseTeamDetailPage';
import EnterpriseMembersPage from './pages/enterprise/EnterpriseMembersPage';
import EnterpriseContactsPage from './pages/enterprise/EnterpriseContactsPage';
import EnterpriseAnalyticsPage from './pages/enterprise/EnterpriseAnalyticsPage';
import EnterpriseInvitesPage from './pages/enterprise/EnterpriseInvitesPage';
import AcceptEnterpriseInvitePage from './pages/enterprise/AcceptEnterpriseInvitePage';
// CSSTransition is still used for the welcome screen overlay, which is toggled
// by state rather than by navigation. Route changes are not animated.
import { CSSTransition } from 'react-transition-group';
import LandingPage from './pages/LandingPage';
import EnterprisePage from './pages/EnterprisePage';
import HowItWorksPage from './pages/HowItWorksPage';
import ProfessionalsPage from './pages/ProfessionalsPage';
import SecurityPrivacyPage from './pages/SecurityPrivacyPage';
import WhyVeloxPage from './pages/WhyVeloxPage';
import ProductPage from './pages/ProductPage';
import HelpPage from './pages/HelpPage';
import MarketingLayout from './components/marketing/MarketingLayout';
import LoginPage from './pages/LoginPage';
import ErrorBoundary from './components/ErrorBoundary';
import RegisterPage from './pages/RegisterPage';
import AccountPage from './pages/AccountPage';
import ContactsPage from './pages/ContactsPage';
import InvitationsPage from './pages/InvitationsPage';
import MapPage from './pages/MapPage';
import NotificationSettings from './pages/NotificationSettings';
import PrivacySettings from './pages/PrivacySettings';
import PolicyPage, { usesPublicChrome } from './pages/PolicyPage'; 
import MyCardPage from './pages/MyCardPage';
import DisplayCardPage from './pages/DisplayCardPage';
import ActivatePage from './pages/ActivatePage';
import GuestSavePage from './pages/GuestSavePage';
import FirstMeetingGuestPage from './pages/FirstMeetingGuestPage';
import EditContactsPage from './pages/EditContactsPage';
import InsightPreferencesPage from './pages/InsightPreferencesPage';
import './styles/App.css';
import './styles/Global.css';
import './styles/theme-variables.css';
import { useTheme } from './hooks/useTheme'; 
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdvancedUserAnalyticsPage from './pages/AdvancedUserAnalyticsPage';
import InsightEventsPage from './pages/InsightEventsPage';
import InsightNewsPage from './pages/InsightNewsPage';
import UpgradePage from './pages/UpgradePage';
import OrderPage from './pages/OrderPage';
import BillingPage from './pages/BillingPage';
import ArchivePage from './pages/ArchivePage';
import HomePage from './pages/HomePage';
import AdminLayout from './pages/admin/AdminLayout';
import AdminEnterprisePage from './pages/admin/AdminEnterprisePage';
import AdminOverviewPage from './pages/admin/AdminOverviewPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminGrowthPage from './pages/admin/AdminGrowthPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminCardsPage from './pages/admin/AdminCardsPage';
import AdminApplicationsPage from './pages/admin/AdminApplicationsPage';
import AdminSecurityPage from './pages/admin/AdminSecurityPage';
import { trackPageView } from './utils/analytics';
import WelcomeScreen from './components/WelcomeScreen';
import { ToastProvider } from './contexts/ToastContext';
import { AccountProvider } from './contexts/AccountContext';
import { EntitlementsProvider } from './contexts/EntitlementsContext';
import { HubNotificationsProvider } from './contexts/HubNotificationsContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import CheckoutReturnHandler from './components/billing/CheckoutReturnHandler';
import EnterpriseSeatsPage from './pages/enterprise/EnterpriseSeatsPage';
import EnterpriseBrandingPage from './pages/enterprise/EnterpriseBrandingPage';
import EnterpriseOrdersPage from './pages/enterprise/EnterpriseOrdersPage';
import AdminCampaignsPage from './pages/admin/AdminCampaignsPage';
import AdminFulfilmentPage from './pages/admin/AdminFulfilmentPage';
import AdminStaffPage from './pages/admin/AdminStaffPage';
import AdminAuditPage from './pages/admin/AdminAuditPage';
import AdminPromotionsPage from './pages/admin/AdminPromotionsPage';
import api from './utils/api';
import {
  isTokenExpired,
  refreshAccessToken,
  clearAuthData,
  scheduleProactiveAccessRefresh,
  initProactiveRefreshLifecycle,
} from './utils/auth';
import { flushGuestSaveQueue, pruneExpired } from './utils/guestOfflineQueue';

// Public marketing pages. These share the MarketingLayout chrome and are
// listed once here so the scroll/overflow handling below cannot drift out of
// step with the route table. /policy is not listed because it only takes the
// marketing chrome when signed out — see isMarketingRoute.
const MARKETING_ROUTES = [
  '/',
  '/enterprise',
  '/how-it-works',
  '/professionals',
  '/security-privacy',
  '/why-velox',
  '/product',
  '/help',
];

const App = () => {
  // Initialize theme early to prevent flash of wrong theme
  useTheme();
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true); // Add auth loading state
  const location = useLocation();

  // Track page views when location changes and reset scroll position
  useEffect(() => {
    trackPageView(location.pathname + location.search);
    
    // Many app pages use inner scroll containers (Home hub layout).
    // Avoid window scroll resets for Contacts, where `.cards-container` owns scroll.
    const shouldResetWindowScroll = !location.pathname.startsWith('/contacts');

    // Scroll to top on navigation (window-scrolling routes only), wrapped in RAF to ensure it happens
    // after React has updated but before the browser paints the transition.
    const rafId = shouldResetWindowScroll
      ? requestAnimationFrame(() => {
          window.scrollTo(0, 0);

          // Reset body and html scroll positions
          if (document.body) {
            document.body.scrollTop = 0;
          }
          if (document.documentElement) {
            document.documentElement.scrollTop = 0;
          }
        })
      : null;
    
    // Reset any overflow modifications from previous pages
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [location.pathname, location.search]);

  useEffect(() => {
    const activationCardId = new URLSearchParams(window.location.search).get('cardId');
    if (activationCardId) {
      localStorage.setItem('activationCardId', activationCardId);
    }
  }, []);

  // Guest offline queue: prune TTL + attempt sync (cookie + server save)
  useEffect(() => {
    try {
      pruneExpired();
    } catch {
      /* ignore */
    }
    flushGuestSaveQueue();
  }, []);

  // Clean up old pending card saves
  useEffect(() => {
    const pendingCardSave = localStorage.getItem('pendingCardSave');
    if (pendingCardSave) {
      try {
        const parsed = JSON.parse(pendingCardSave);
        // Handle both array format (new) and single card format (old)
        const cardDataArray = Array.isArray(parsed) ? parsed : [parsed];
        
        // Filter cards that are still valid (within 24 hours)
        const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);
        const validCards = cardDataArray.filter(card => card.timestamp > twentyFourHoursAgo);
        
        if (validCards.length === 0) {
          // All cards expired, remove entirely
          localStorage.removeItem('pendingCardSave');
        } else if (validCards.length < cardDataArray.length) {
          // Some cards expired, update with only valid ones
          localStorage.setItem('pendingCardSave', JSON.stringify(validCards));
        }
        // If all cards are valid, no action needed
      } catch (error) {
        localStorage.removeItem('pendingCardSave');
      }
    }
  }, []);

  useEffect(() => {
    // Validate token with server instead of just checking localStorage
    const validateToken = async () => {
      setIsAuthLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setIsAuthenticated(false);
        setIsAuthLoading(false);
        return;
      }

      // First check if token is expired on client side
      if (isTokenExpired(token)) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          setIsAuthenticated(true);
          scheduleProactiveAccessRefresh();
          initProactiveRefreshLifecycle();
        } else {
          setIsAuthenticated(false);
        }
        setIsAuthLoading(false);
        return;
      }

      try {
        // Validate token with server
        await api.get('/api/user/account', {
          timeout: 15000
        });
        setIsAuthenticated(true);
        scheduleProactiveAccessRefresh();
        initProactiveRefreshLifecycle();
        setIsAuthLoading(false);
      } catch (error) {
        // Only clear tokens for actual authentication failures, not temporary server issues
        const status = error.response?.status;
        const isAuthError = status === 401 || status === 403;
        const isTokenError = error.response?.data?.error?.toLowerCase?.()?.includes('token') || 
                            error.response?.data?.error?.toLowerCase?.()?.includes('expired') ||
                            error.response?.data?.error?.toLowerCase?.()?.includes('invalid');
        
        if (isAuthError || isTokenError) {
          clearAuthData();
          setIsAuthenticated(false);
        } else {
          // For other errors, try refresh first
          const newToken = await refreshAccessToken();
          if (newToken) {
            setIsAuthenticated(true);
            scheduleProactiveAccessRefresh();
            initProactiveRefreshLifecycle();
          } else {
            setIsAuthenticated(false);
          }
        }
        setIsAuthLoading(false);
      }
    };

    validateToken();
  }, []);

  // Check for returning user session and show welcome screen
  useEffect(() => {
    const lastVisit = localStorage.getItem('lastVisitTime');
    const currentTime = Date.now();
    const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
    
    // Check if it's been more than 1 hour since last visit
    if (!lastVisit || (currentTime - parseInt(lastVisit)) > oneHour) {
      // Don't show on specific routes
      const excludedRoutes = [
        '/',
        '/memberships',
        '/register', 
        '/forgot-password',
        '/reset-password',
        '/my-display-card',
        '/display-card',
        '/nfc',
        '/c/',
        '/guest'
      ];
      
      const isExcludedRoute = excludedRoutes.some(route => 
        location.pathname.startsWith(route)
      );
      
      if (!isExcludedRoute) {
        setShowWelcomeScreen(true);
        setTimeout(() => setShowWelcomeScreen(false), 3500); // 2.5 seconds
      }
    }
    
    // Update last visit time
    localStorage.setItem('lastVisitTime', currentTime.toString());
  }, [location.pathname]);

  // Check if current page is an auth page for animation purposes
  const isAuthPage = ['/login', '/register', '/forgot-password'].includes(location.pathname) || 
                     location.pathname.startsWith('/reset-password');
  
  // Check if current page is an unauthenticated route (marketing pages, auth pages or display card pages)
  // /policy is both: signed out it renders inside MarketingLayout and needs the
  // same document-flow layout as the rest of the public site, and signed in it
  // is an ordinary app page. PolicyPage owns that decision so the two cannot
  // disagree.
  const isMarketingRoute =
    MARKETING_ROUTES.includes(location.pathname) ||
    (location.pathname.startsWith('/policy') && usesPublicChrome());
  const isUnauthenticatedRoute = isMarketingRoute ||
                                  isAuthPage || 
                                  location.pathname.startsWith('/display-card') ||
                                  location.pathname.startsWith('/my-display-card') ||
                                  location.pathname.startsWith('/nfc/') ||
                                  location.pathname.startsWith('/c/') ||
                                  location.pathname.startsWith('/guest/');
  
  // Set --vh for layouts that cannot use 100dvh (fallback in HomeHub.module.css).
  // Single listener + visualViewport: iOS standalone/toolbar changes update innerHeight.
  useEffect(() => {
    const setVh = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    };
    setVh();
    window.addEventListener('resize', setVh);
    window.visualViewport?.addEventListener('resize', setVh);
    return () => {
      window.removeEventListener('resize', setVh);
      window.visualViewport?.removeEventListener('resize', setVh);
    };
  }, []);

  
  useEffect(() => {
    if (isMarketingRoute) {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
      document.body.style.height = 'auto';
      document.documentElement.style.height = 'auto';
    } else {
      // Reset to default for other pages
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.height = '';
      document.documentElement.style.height = '';
    }
    
    return () => {
      // Cleanup on unmount or route change
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.height = '';
      document.documentElement.style.height = '';
    };
    // pathname is a dependency as well as isMarketingRoute so that the reset
    // still runs on every navigation, as it did before /policy joined the
    // marketing routes.
  }, [isMarketingRoute, location.pathname]);

  return (
    <ToastProvider>
      {/* The signed-in user, fetched once and shared. The shell, the nav and
          the account hub all read their identity from here rather than each
          calling /api/user/account for itself. */}
      <AccountProvider>
      <WorkspaceProvider>
      {/* Hub bell count, fetched once so changing page does not rebuild it. */}
      <HubNotificationsProvider>
      {/* What this account may use, resolved by the server. Gates in the UI read
          from here; every paid endpoint still checks for itself. */}
      <EntitlementsProvider>
      <CheckoutReturnHandler />
      <div className={`app-container ${isUnauthenticatedRoute ? 'app-container-unauthenticated' : ''} ${isMarketingRoute ? 'app-container-marketing' : ''}`}> 
      
      <div className={`app-content ${isMarketingRoute ? 'app-content-marketing' : ''}`}>
        {/* Navigation is deliberately not animated.
            Route components are all imported eagerly, so the incoming page is
            already in memory and can render in the same frame as the click.
            Any transition here is therefore pure added latency rather than
            cover for loading work: SwitchTransition's out-in mode in particular
            would not even begin mounting the new page until the old one had
            finished leaving. Marketing and admin already navigated instantly;
            this is the rest of the app matching them. */}
            <div>
              {/* Keyed on pathname so the boundary resets when the user navigates:
                  a crash on one route must not leave every later route showing the
                  fallback. The root boundary in index.js remains the last resort. */}
              <ErrorBoundary key={location.pathname}>
              <Routes location={location}>
                {/* Public marketing site. MarketingLayout supplies the shared
                    header, footer and enquiry modal. */}
                <Route path="/" element={<MarketingLayout><LandingPage /></MarketingLayout>} />
                <Route path="/enterprise" element={<MarketingLayout><EnterprisePage /></MarketingLayout>} />
                <Route path="/how-it-works" element={<MarketingLayout><HowItWorksPage /></MarketingLayout>} />
                <Route path="/professionals" element={<MarketingLayout><ProfessionalsPage /></MarketingLayout>} />
                <Route path="/security-privacy" element={<MarketingLayout><SecurityPrivacyPage /></MarketingLayout>} />
                <Route path="/why-velox" element={<MarketingLayout><WhyVeloxPage /></MarketingLayout>} />
                <Route path="/about" element={<Navigate to="/why-velox" replace />} />
                <Route path="/product" element={<MarketingLayout><ProductPage /></MarketingLayout>} />
                <Route path="/help" element={<MarketingLayout><HelpPage /></MarketingLayout>} />

                {/* /individual was the previous path for the same page. Kept as
                    a redirect so links already shared, and the sitemap Google
                    has crawled, resolve rather than falling through to /login. */}
                <Route path="/individual" element={<Navigate to="/professionals" replace />} />

                {/* Legal documents — public, and deliberately not behind the
                    auth gate. UK GDPR Article 13 requires the privacy
                    information to be available at the point of collection, so
                    every pre-auth form (register, sign in, the enquiry modal and
                    guest capture) links here, as does the marketing footer.
                    PolicyPage picks its own chrome: marketing header and footer
                    when signed out, the in-app view with a back link to
                    Settings when signed in. */}
                <Route path="/policy" element={<PolicyPage />} />
                <Route path="/policy/:section" element={<PolicyPage />} />

                {/* Retired members-club page. The route is kept so existing
                    links resolve rather than falling through to /login; the
                    component and its backend remain in place for future use. */}
                <Route path="/memberships" element={<Navigate to="/" replace />} />

                {/* Enterprise app (dashboard) — protected */}
                <Route path="/enterprise-app" element={
                  isAuthLoading ? <div></div> :
                  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <EnterpriseSelectPage /> : <Navigate to="/login" />
                } />
                <Route path="/enterprise-app/:enterpriseId" element={
                  isAuthLoading ? <div></div> :
                  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <EnterpriseAppLayout /> : <Navigate to="/login" />
                }>
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard" element={<EnterpriseDashboardPage />} />
                  <Route path="teams">
                    <Route index element={<EnterpriseTeamsPage />} />
                    <Route path=":teamId" element={<EnterpriseTeamDetailPage />} />
                  </Route>
                  <Route path="members" element={<EnterpriseMembersPage />} />
                  <Route path="contacts" element={<EnterpriseContactsPage />} />
                  <Route path="analytics" element={<EnterpriseAnalyticsPage />} />
                  <Route path="invites" element={<EnterpriseInvitesPage />} />
                  <Route path="seats" element={<EnterpriseSeatsPage />} />
                  <Route path="branding" element={<EnterpriseBrandingPage />} />
                  <Route path="orders" element={<EnterpriseOrdersPage />} />
                </Route>
                <Route path="/enterprise-invite/:token" element={<AcceptEnterpriseInvitePage />} />
                
                {/* Auth routes*/}
                <Route path="/login" element={<LoginPage setAuth={setIsAuthenticated} />} />
                <Route path="/register" element={<RegisterPage setAuth={setIsAuthenticated} />} />

                {/* Admin routes — shared layout; platform admin enforced in AdminLayout + backend */}
                <Route path="/admin" element={
                  isAuthLoading ? <div></div> :
                  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <AdminLayout /> : <Navigate to="/login" />
                }>
                  <Route index element={<Navigate to="/admin/overview" replace />} />
                  <Route path="overview" element={<AdminOverviewPage />} />
                  <Route path="users" element={<AdminUsersPage />} />
                  <Route path="users/:userTab" element={<Navigate to="/admin/users" replace />} />
                  <Route path="growth" element={<AdminGrowthPage />} />
                  <Route path="enterprise" element={<AdminEnterprisePage />} />
                  <Route path="analytics/orders" element={<Navigate to="/admin/orders" replace />} />
                  <Route path="analytics/applications" element={<Navigate to="/admin/applications" replace />} />
                  <Route path="analytics" element={<Navigate to="/admin/overview" replace />} />
                  <Route path="orders" element={<AdminOrdersPage />} />
                  <Route path="cards" element={<AdminCardsPage />} />
                  <Route path="fulfilment" element={<AdminFulfilmentPage />} />
                  <Route path="campaigns" element={<AdminCampaignsPage />} />
                  <Route path="applications" element={<AdminApplicationsPage />} />
                  <Route path="security" element={<AdminSecurityPage />} />
                  <Route path="staff" element={<AdminStaffPage />} />
                  <Route path="audit" element={<AdminAuditPage />} />
                  <Route path="promotions" element={<AdminPromotionsPage />} />
                </Route>
                {/* Backward-compatible redirects for the former standalone routes */}
                <Route path="/admin/dashboard" element={<Navigate to="/admin/overview" replace />} />
                <Route path="/security" element={<Navigate to="/admin/security" replace />} />

                {/* Protected routes */}
<Route path="/home" element={
  isAuthLoading ? <div></div> : // Silent auth check - no loading message
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <HomePage /> : <Navigate to="/login" />
} />
<Route path="/contacts" element={
  isAuthLoading ? <div></div> : // Silent auth check - no loading message
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <ContactsPage /> : <Navigate to="/login" />
} />
<Route path="/guest/first-meeting" element={
  isAuthLoading ? <div></div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <FirstMeetingGuestPage /> : <Navigate to="/login" />
} />
<Route path="/invitations" element={
  isAuthLoading ? <div className="auth-loading">Checking authentication...</div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <InvitationsPage /> : <Navigate to="/login" />
} />
<Route path="/map" element={
  isAuthLoading ? <div className="auth-loading">Checking authentication...</div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <MapPage /> : <Navigate to="/login" />
} />
<Route path="/my-card" element={
  isAuthLoading ? <div className="auth-loading">Checking authentication...</div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <MyCardPage /> : <Navigate to="/login" />
} />
{/* The account hub. /profile, /account-settings, /settings and
    /delete-account were four doors into three overlapping pages; they now
    resolve here so existing links, bookmarks and emails still land
    somewhere useful. */}
<Route path="/account" element={
  isAuthLoading ? <div className="auth-loading">Checking authentication...</div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <AccountPage /> : <Navigate to="/login" />
} />
<Route path="/account-settings" element={<Navigate to="/account" replace />} />
<Route path="/profile" element={<Navigate to="/account" replace />} />
<Route path="/settings" element={<Navigate to="/account" replace />} />
<Route path="/delete-account" element={<Navigate to="/account" replace />} />
<Route path="/notification-settings" element={
  isAuthLoading ? <div className="auth-loading">Checking authentication...</div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <NotificationSettings /> : <Navigate to="/login" />
} />
<Route path="/privacy-settings" element={
  isAuthLoading ? <div className="auth-loading">Checking authentication...</div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <PrivacySettings /> : <Navigate to="/login" />
} />
<Route path="/edit-contacts" element={
  isAuthLoading ? <div className="auth-loading">Checking authentication...</div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <EditContactsPage /> : <Navigate to="/login" />
} />
<Route path="/insights" element={
  isAuthLoading ? <div className="auth-loading">Checking authentication...</div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <Navigate to="/insights/news" replace /> : <Navigate to="/login" />
} />
<Route path="/insights/analytics" element={<Navigate to="/advanced-analytics" replace />} />
<Route path="/advanced-analytics" element={
  isAuthLoading ? <div className="auth-loading">Checking authentication...</div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <AdvancedUserAnalyticsPage /> : <Navigate to="/login" />
} />
<Route path="/insights/enhanced-analytics" element={<Navigate to="/advanced-analytics" replace />} />
<Route path="/insights/events" element={
  isAuthLoading ? <div className="auth-loading">Checking authentication...</div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <InsightEventsPage /> : <Navigate to="/login" />
} />
<Route path="/insights/news" element={
  isAuthLoading ? <div className="auth-loading">Checking authentication...</div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <InsightNewsPage /> : <Navigate to="/login" />
} />
<Route path="/analytics" element={<Navigate to="/advanced-analytics" replace />} />
<Route path="/archive" element={
  isAuthLoading ? <div className="auth-loading">Checking authentication...</div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <ArchivePage /> : <Navigate to="/login" />
} />
<Route path="/upgrade" element={
  isAuthLoading ? <div className="auth-loading">Checking authentication...</div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <UpgradePage /> : <Navigate to="/login" />
} />
<Route path="/order" element={
  isAuthLoading ? <div className="auth-loading">Checking authentication...</div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <OrderPage /> : <Navigate to="/login" />
} />
<Route path="/billing" element={
  isAuthLoading ? <div className="auth-loading">Checking authentication...</div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <BillingPage /> : <Navigate to="/login" />
} />
<Route path="/insight-preferences" element={
  isAuthLoading ? <div className="auth-loading">Checking authentication...</div> :
  isAuthenticated && localStorage.getItem('verificationPending') !== 'true' ? <InsightPreferencesPage /> : <Navigate to="/login" />
} />

                {/* Unprotected routes 1 */}
                <Route path="/my-display-card/:nfcUID" element={<DisplayCardPage />} />
                <Route path="/display-card/:nfcUID" element={<DisplayCardPage />} />
                <Route path="/nfc/:nfcUID" element={<DisplayCardPage />} />
                <Route path="/c/:userId" element={<DisplayCardPage />} />
                {/* Manual card entry, for someone typing the ID from their order
                    email. The page existed but was never routed, so every
                    /activate?cardId= link in emails and admin was a 404. */}
                <Route path="/activate" element={<ActivatePage />} />
                <Route path="/guest/save" element={<GuestSavePage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

                {/* Catch-all route */}
                <Route path="*" element={<Navigate to="/login" />} />
              </Routes>
              </ErrorBoundary>
              <CSSTransition
                in={showWelcomeScreen}
                timeout={500}
                classNames="welcome-fade"
                unmountOnExit
              >
                <WelcomeScreen />
              </CSSTransition>
            </div>
        </div>
      </div>
      </EntitlementsProvider>
      </HubNotificationsProvider>
      </WorkspaceProvider>
      </AccountProvider>
    </ToastProvider>
  );
};

export default App;

